import { AmazonPaySdkState } from "@swiggy-private/payment-ui";
import { AMAZON_PAY_STATE_STALE_REASONS } from "@payment-client/constants/AmazonPay";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import {
    getAmazonPayBalanceFromPaymentsResponse,
    isAmazonPayLinked,
    isAmazonPayMethodAvailable,
    isNewAmazonPaySDKAvailable,
} from "@payment-client/helpers/WalletHelper";
import { getAmazonPayLaterInfo, AmazonPayLaterField } from "@payment-client/helpers/PaylaterHelper";

interface AmazonPayStaleCheckParams {
    state: AmazonPaySdkState;
    hasNewPaymentResponse: boolean;
    isApbLinked: boolean | undefined;
    isAplLinked: boolean | undefined;
    apayBalance: string | undefined;
    isAPayMethodAvailable: boolean;
    isAmazonPayLaterAvailable: boolean;
    amountToPay: number;
    aplCreditLimit: string | undefined;
}

/**
 * Returns the stale reason if cached Amazon Pay SDK state is out of sync
 * with the latest GPO response, or `null` if state is still valid.
 */
export const getAmazonPayStateStaleReason = ({
    state,
    hasNewPaymentResponse,
    isApbLinked,
    isAplLinked,
    apayBalance,
    isAPayMethodAvailable,
    isAmazonPayLaterAvailable,
    amountToPay,
    aplCreditLimit,
}: AmazonPayStaleCheckParams): string | null => {
    const isApbLinkedMismatch = hasNewPaymentResponse && state.linked !== isApbLinked;
    if (isApbLinkedMismatch) return AMAZON_PAY_STATE_STALE_REASONS.LINKED_MISMATCH;

    const isAplLinkedMismatch = hasNewPaymentResponse && !!state.apl?.linked !== !!isAplLinked;
    if (isAplLinkedMismatch) return AMAZON_PAY_STATE_STALE_REASONS.APL_LINKED_MISMATCH;

    const isBalanceMismatch =
        hasNewPaymentResponse && state.linked && isApbLinked && apayBalance && state.balance !== apayBalance;
    if (isBalanceMismatch) return AMAZON_PAY_STATE_STALE_REASONS.BALANCE_MISMATCH;

    const isAvailabilityMismatch =
        hasNewPaymentResponse && state.available && !isAPayMethodAvailable && !isAmazonPayLaterAvailable;
    if (isAvailabilityMismatch) return AMAZON_PAY_STATE_STALE_REASONS.AVAILABILITY_MISMATCH;

    const isAplCreditLimitMismatch =
        hasNewPaymentResponse &&
        state.apl?.linked &&
        isAplLinked &&
        aplCreditLimit &&
        state.apl?.creditLimit !== aplCreditLimit;
    if (isAplCreditLimitMismatch) return AMAZON_PAY_STATE_STALE_REASONS.APL_CREDIT_LIMIT_MISMATCH;

    const isAplCreditSufficiencyStale =
        state.apl?.linked &&
        state.apl?.creditLimit != null &&
        state.apl?.isCreditLimitSufficient !== Number(state.apl?.creditLimit) >= amountToPay;
    if (isAplCreditSufficiencyStale) return AMAZON_PAY_STATE_STALE_REASONS.APL_CREDIT_SUFFICIENCY_STALE;

    const isBalanceSufficiencyStale =
        state.linked && state.balance != null && state.isBalanceSufficient !== Number(state.balance) >= amountToPay;
    if (isBalanceSufficiencyStale) return AMAZON_PAY_STATE_STALE_REASONS.BALANCE_SUFFICIENCY_STALE;

    return null;
};

export const buildAplState = (
    linked: boolean,
    creditLimit: string | undefined,
    amountToPay: number,
    opts?: { keyFactStatementUrl?: string; disableMessage?: string },
): NonNullable<AmazonPaySdkState["apl"]> => ({
    linked,
    creditLimit,
    isCreditLimitSufficient: Number(creditLimit) >= amountToPay,
    keyFactStatementUrl: opts?.keyFactStatementUrl,
    disableMessage: opts?.disableMessage,
});

export interface AmazonPayDerivedState {
    isAPayMethodAvailable: boolean;
    isAmazonPayLaterAvailable: boolean;
    isNewApaySDKAvailable: boolean;
    isApbLinked: boolean | undefined;
    isAplLinked: boolean | undefined;
    isAnyAmazonPayLinked: boolean | undefined;
    apayBalance: string | undefined;
    aplCreditLimit: string | undefined;
    aplKeyFactStatementUrl: string | undefined;
}

export const getAmazonPayDerivedState = (paymentResponse: GetPaymentMethodsResponse): AmazonPayDerivedState => {
    const isAPayMethodAvailable = isAmazonPayMethodAvailable(paymentResponse);
    const isAmazonPayLaterAvailable = getAmazonPayLaterInfo(paymentResponse, AmazonPayLaterField.ENABLED_STATUS);
    const isNewApaySDKAvailable = isNewAmazonPaySDKAvailable(paymentResponse) || isAmazonPayLaterAvailable;
    const isApbLinked = isNewApaySDKAvailable ? isAmazonPayLinked(paymentResponse) : false;
    const isAplLinked = isAmazonPayLaterAvailable
        ? getAmazonPayLaterInfo(paymentResponse, AmazonPayLaterField.LINKED_STATUS)
        : false;
    const isAnyAmazonPayLinked = isApbLinked || isAplLinked;
    const apayBalance = isApbLinked ? getAmazonPayBalanceFromPaymentsResponse(paymentResponse) : undefined;
    const aplCreditLimit = isAplLinked
        ? getAmazonPayLaterInfo(paymentResponse, AmazonPayLaterField.CREDIT_LIMIT)
        : undefined;
    const aplKeyFactStatementUrl = isAplLinked
        ? getAmazonPayLaterInfo(paymentResponse, AmazonPayLaterField.KEY_FACT_STATEMENT_URL)
        : undefined;

    return {
        isAPayMethodAvailable,
        isAmazonPayLaterAvailable,
        isNewApaySDKAvailable,
        isApbLinked,
        isAplLinked,
        isAnyAmazonPayLinked,
        apayBalance,
        aplCreditLimit,
        aplKeyFactStatementUrl,
    };
};
